/**
 * Overlay Panels Module
 * Handles panel toggle system, icon button interactions, and panel state management
 */

import { elements } from './dom-cache.js';
import { playlistState } from './state.js';
import { cleanupModal } from './cleanup-modal.js';
import { authService } from '../auth/auth-service.js';
import { lockBodyScroll, unlockBodyScroll } from '../lib/overflow-lock.js';

// Panel configuration
const panels = {
  visualizer: { id: 'visualizerPanel', closesOnSelect: true },
  playlist: { id: 'playlistPanel', closesOnSelect: false },
  generator: { id: 'generatorPanel', closesOnSelect: false },
  library: { id: 'libraryPanel', closesOnSelect: false },
  export: { id: 'exportPanel', closesOnSelect: false },
  account: { id: 'accountPanel', closesOnSelect: false }
};

// Current panel state
let activePanel = null;
let settingsOpen = false;

/**
 * Initialize overlay panels system
 */
export function initOverlayPanels() {
  // Get icon buttons
  const iconButtons = document.querySelectorAll('.icon-sidebar .icon-btn[data-panel]');
  const settingsBtn = document.querySelector('.icon-sidebar .icon-btn[data-panel="settings"]');
  const liveBtn = document.querySelector('.icon-sidebar .icon-btn[data-action="live"]');
  const backdrop = document.getElementById('overlayBackdrop');
  const settingsOverlay = document.getElementById('settingsOverlay');
  const settingsCollapseBtn = document.querySelector('.settings-collapse-btn');

  // Icon button click handlers
  iconButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      // IMPORTANT: Stop propagation to prevent document click handler from closing the panel.
      // When certain panels open (e.g., export), they trigger lucide.createIcons() which
      // replaces ALL SVG icons on the page. This detaches the clicked SVG from the DOM
      // before the document click handler runs. Since e.target is now orphaned (parentElement
      // is null), contains() and closest() both fail, and the document handler incorrectly
      // thinks the click was outside the sidebar and closes the panel immediately.
      e.stopPropagation();

      const panelName = btn.dataset.panel;

      if (panelName === 'settings') {
        toggleSettings();
      } else if (panelName === 'playlist') {
        handlePlaylistClick();
      } else {
        togglePanel(panelName);
      }
    });
  });

  // Live mode direct toggle
  if (liveBtn) {
    liveBtn.addEventListener('click', () => {
      // Dispatch custom event for live-mode.js to handle
      document.dispatchEvent(new CustomEvent('toggleLiveMode'));
    });
  }

  // Settings collapse button
  if (settingsCollapseBtn) {
    settingsCollapseBtn.addEventListener('click', () => {
      toggleSettings();
    });
  }

  // Backdrop click closes panels
  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      e.stopPropagation();
      closeAllPanels();
    });
  }

  // ESC key closes panels (but not if cleanup modal is open)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Don't close panels if cleanup modal is open - let it handle ESC
      if (cleanupModal.isOpen) return;

      if (activePanel) {
        closeAllPanels();
      } else if (settingsOpen) {
        toggleSettings();
      }
    }
  });

  // Close panel when clicking outside (but not on icon sidebar or cleanup modal)
  document.addEventListener('click', (e) => {
    if (!activePanel) return;

    const panel = document.getElementById(panels[activePanel]?.id);
    const iconSidebar = document.querySelector('.icon-sidebar');
    const cleanupModalEl = document.getElementById('cleanupModal');

    // Don't close if clicking inside the panel, icon sidebar, or cleanup modal
    const isInsidePanel = panel && panel.contains(e.target);
    const isInsideSidebar = iconSidebar && iconSidebar.contains(e.target);
    const isInsideCleanupModal = cleanupModalEl && cleanupModalEl.contains(e.target);

    if (!isInsidePanel && !isInsideSidebar && !isInsideCleanupModal) {
      closeAllPanels();
    }
  });

  // Panel close buttons
  document.querySelectorAll('[data-close-panel]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeAllPanels();
    });
  });

  // Listen for live mode state changes to sync drop modal visibility
  document.addEventListener('liveModeEntered', () => {
    hideDropModal();
    updateDropzoneLiveButton(true);
    updatePlaylistLiveModeButton(true);
  });

  document.addEventListener('liveModeExited', () => {
    updateDropzoneLiveButton(false);
    updatePlaylistLiveModeButton(false);
    syncPlaylistVisibility();
  });

  // Playlist panel Live Mode button (for mobile)
  const playlistLiveModeBtn = document.getElementById('playlistLiveModeBtn');
  if (playlistLiveModeBtn) {
    playlistLiveModeBtn.addEventListener('click', () => {
      closeAllPanels();
      const isLiveMode = document.body.classList.contains('live-mode-active');
      if (!isLiveMode) {
        document.dispatchEvent(new CustomEvent('toggleLiveMode'));
      }
    });
  }

  console.log('[OverlayPanels] Initialized');
}

/**
 * Toggle a specific panel
 */
export function togglePanel(panelName) {
  const config = panels[panelName];
  if (!config) return;

  const panel = document.getElementById(config.id);
  const btn = document.querySelector(`.icon-btn[data-panel="${panelName}"]`);
  const backdrop = document.getElementById('overlayBackdrop');

  if (!panel) return;

  // If this panel is already open, close it
  if (activePanel === panelName) {
    closeAllPanels();
    return;
  }

  // Close any other open panel first
  if (activePanel) {
    const oldPanel = document.getElementById(panels[activePanel]?.id);
    const oldBtn = document.querySelector(`.icon-btn[data-panel="${activePanel}"]`);
    if (oldPanel) oldPanel.classList.remove('open');
    if (oldBtn) oldBtn.classList.remove('active');
  }

  // Open new panel
  panel.classList.add('open');
  if (btn) btn.classList.add('active');
  if (backdrop) backdrop.classList.add('visible');
  activePanel = panelName;

  // Dispatch event
  document.dispatchEvent(new CustomEvent('panelOpened', { detail: { panel: panelName } }));
}

/**
 * Close all overlay panels
 */
export function closeAllPanels() {
  Object.keys(panels).forEach(panelName => {
    const panel = document.getElementById(panels[panelName].id);
    const btn = document.querySelector(`.icon-btn[data-panel="${panelName}"]`);
    if (panel) panel.classList.remove('open');
    if (btn) btn.classList.remove('active');
  });

  const backdrop = document.getElementById('overlayBackdrop');
  if (backdrop) backdrop.classList.remove('visible');

  activePanel = null;

  document.dispatchEvent(new CustomEvent('panelsClosed'));
}

/**
 * Close panel after selection (for auto-close panels like visualizer)
 */
export function closePanelOnSelect(panelName) {
  const config = panels[panelName];
  if (config?.closesOnSelect && activePanel === panelName) {
    closeAllPanels();
  }
}

/**
 * Toggle settings overlay
 */
export function toggleSettings() {
  const settingsOverlay = document.getElementById('settingsOverlay');
  const settingsBtn = document.querySelector('.icon-btn[data-panel="settings"]');

  if (!settingsOverlay) return;

  settingsOpen = !settingsOpen;

  if (settingsOpen) {
    settingsOverlay.classList.add('open');
    if (settingsBtn) settingsBtn.classList.add('active');
  } else {
    settingsOverlay.classList.remove('open');
    if (settingsBtn) settingsBtn.classList.remove('active');
  }

  document.dispatchEvent(new CustomEvent('settingsToggled', { detail: { open: settingsOpen } }));
}

/**
 * Handle playlist icon click
 * Shows drop modal if no files, otherwise shows playlist panel
 * If drop modal is already visible, triggers file selection
 */
function handlePlaylistClick() {
  const hasFiles = playlistState.playlist && playlistState.playlist.length > 0;

  if (hasFiles) {
    togglePanel('playlist');
  } else {
    const dropModal = document.getElementById('dropModal');
    const isModalAlreadyOpen = dropModal && dropModal.classList.contains('open');

    if (isModalAlreadyOpen) {
      // Modal already visible - trigger file selection directly
      const fileInput = elements.fileInput || document.getElementById('fileInput');
      if (fileInput) {
        fileInput.click();
      }
    } else {
      // Close any open panels (e.g., visualizer sidebar) before showing drop modal
      closeAllPanels();
      showDropModal();
    }
  }
}

/**
 * Show drop zone modal
 */
export function showDropModal() {
  const modal = document.getElementById('dropModal');
  if (modal) {
    modal.classList.add('open');
    lockBodyScroll();
    // Select playlist icon when drop modal is open
    const playlistBtn = document.querySelector('.icon-btn[data-panel="playlist"]');
    if (playlistBtn) playlistBtn.classList.add('active');
  }
}

/**
 * Hide drop zone modal
 */
export function hideDropModal() {
  const modal = document.getElementById('dropModal');
  if (modal) {
    modal.classList.remove('open');
    unlockBodyScroll();
    // Deselect playlist icon when drop modal is closed
    const playlistBtn = document.querySelector('.icon-btn[data-panel="playlist"]');
    if (playlistBtn) playlistBtn.classList.remove('active');
  }
}

/**
 * Initialize drop modal handlers
 */
export function initDropModal() {
  const modal = document.getElementById('dropModal');
  const liveBtn = document.getElementById('dropzoneLiveBtn');

  // Live button in dropzone - behavior depends on current live mode state
  if (liveBtn) {
    liveBtn.addEventListener('click', () => {
      const isLiveMode = document.body.classList.contains('live-mode-active');

      if (isLiveMode) {
        // Already in live mode - just hide modal (return to live)
        hideDropModal();
      } else {
        // Not in live mode - enter live mode (modal will hide via liveModeEntered event)
        document.dispatchEvent(new CustomEvent('toggleLiveMode'));
      }
    });
  }

  // ESC key closes modal only if we have a playlist or are in live mode
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal?.classList.contains('open')) {
      // Only close if there's content to show (playlist has items or live mode active)
      const hasPlaylist = playlistState.playlist && playlistState.playlist.length > 0;
      const isLiveMode = document.body.classList.contains('live-mode-active');
      if (hasPlaylist || isLiveMode) {
        hideDropModal();
      }
    }
  });

  // Initialize button text based on current state
  updateDropzoneLiveButton(document.body.classList.contains('live-mode-active'));
}

/**
 * Update dropzone live button text based on live mode state
 */
export function updateDropzoneLiveButton(isLiveMode) {
  const liveBtn = document.getElementById('dropzoneLiveBtn');
  if (!liveBtn) return;

  const textContent = ' Live Mode';

  // Find the existing icon (could be <i data-lucide> or rendered <svg>)
  const existingIcon = liveBtn.querySelector('svg, i[data-lucide]');

  if (existingIcon) {
    // Clone the icon to preserve it
    const iconClone = existingIcon.cloneNode(true);
    liveBtn.innerHTML = '';
    liveBtn.appendChild(iconClone);
    liveBtn.appendChild(document.createTextNode(textContent));

    // Re-render Lucide icons if needed (in case we cloned an unrendered <i>)
    if (typeof lucide !== 'undefined' && iconClone.tagName === 'I') {
      lucide.createIcons({ nodes: [liveBtn] });
    }
  } else {
    liveBtn.textContent = textContent;
  }
}

/**
 * Sync playlist-related UI visibility based on app state.
 * Manages drop modal, transport bar, and export button reactively.
 * Called on every playlistUpdated event and on live mode transitions.
 */
export function syncPlaylistVisibility() {
  const hasPlaylist = playlistState.playlist && playlistState.playlist.length > 0;
  const isLiveMode = document.body.classList.contains('live-mode-active');

  if (!isLiveMode && !hasPlaylist) {
    showDropModal();
    hideTransportBar();
    updateExportButtonState(false);
  } else if (hasPlaylist) {
    hideDropModal();
    showTransportBar();
    updateExportButtonState(true);
  } else {
    // Live mode, no playlist — drop modal already hidden by liveModeEntered handler
    hideDropModal();
  }
}

/**
 * Update playlist panel Live Mode button (for mobile)
 */
export function updatePlaylistLiveModeButton(isLiveMode) {
  const btn = document.getElementById('playlistLiveModeBtn');
  if (!btn) return;

  btn.classList.toggle('live-active', isLiveMode);

  // Update button text
  const span = btn.querySelector('span');
  if (span) {
    span.textContent = 'Live Mode';
  }
}

/**
 * Initialize account panel handlers
 */
export function initAccountPanel(authUI) {
  const panelLoginBtn = document.getElementById('panelLoginBtn');
  const panelSignupBtn = document.getElementById('panelSignupBtn');
  const panelLogoutBtn = document.getElementById('panelLogoutBtn');
  const panelUpgradeBtn = document.getElementById('panelUpgradeBtn');
  const panelAccountBtn = document.getElementById('panelAccountBtn');
  const panelHistoryBtn = document.getElementById('panelHistoryBtn');

  // Login button
  if (panelLoginBtn) {
    panelLoginBtn.addEventListener('click', () => {
      closeAllPanels();
      if (authUI) {
        authUI.showModal('login');
      }
    });
  }

  // Signup button
  if (panelSignupBtn) {
    panelSignupBtn.addEventListener('click', () => {
      closeAllPanels();
      if (authUI) {
        authUI.showModal('signup');
      }
    });
  }

  // Logout button
  if (panelLogoutBtn) {
    panelLogoutBtn.addEventListener('click', async () => {
      closeAllPanels();
      try {
        await authService.signOut();
        window.location.reload();
      } catch (error) {
        console.error('Logout error:', error);
      }
    });
  }

  // Upgrade button
  if (panelUpgradeBtn) {
    panelUpgradeBtn.addEventListener('click', () => {
      closeAllPanels();
      if (authUI && authUI.onUpgradeClick) {
        authUI.onUpgradeClick();
      }
    });
  }

  // Account Settings button
  if (panelAccountBtn) {
    panelAccountBtn.addEventListener('click', () => {
      closeAllPanels();
      if (authUI && authUI.onAccountClick) {
        authUI.onAccountClick();
      }
    });
  }

  // Credit History button
  if (panelHistoryBtn) {
    panelHistoryBtn.addEventListener('click', () => {
      closeAllPanels();
      if (authUI && authUI.onHistoryClick) {
        authUI.onHistoryClick();
      }
    });
  }
}

/**
 * Update account panel UI based on auth state
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {Object} user - User object
 * @param {string} tier - User's tier
 * @param {boolean} freeMode - Whether Free Mode is active (hides tier-related UI)
 */
export function updateAccountPanel(isAuthenticated, user, tier, freeMode = false) {
  const loggedOut = document.getElementById('accountLoggedOut');
  const loggedIn = document.getElementById('accountLoggedIn');
  const panelUserEmail = document.getElementById('panelUserEmail');
  const panelTierBadge = document.getElementById('panelTierBadge');
  const panelUpgradeBtn = document.getElementById('panelUpgradeBtn');
  const panelHistoryBtn = document.getElementById('panelHistoryBtn');
  const accountIconBtn = document.getElementById('accountIconBtn');

  if (!loggedOut || !loggedIn) return;

  if (isAuthenticated && user) {
    loggedOut.style.display = 'none';
    loggedIn.style.display = 'block';

    if (panelUserEmail) {
      panelUserEmail.textContent = user.email || 'User';
    }

    if (panelTierBadge && tier) {
      const tierLabels = {
        anonymous: 'GUEST',
        free: 'FREE',
        pro: 'PRO',
        enterprise: 'ENTERPRISE'
      };
      panelTierBadge.textContent = tierLabels[tier] || tier.toUpperCase();
      panelTierBadge.className = `tier-badge tier-${tier}`;
    }

    // In Free Mode, hide tier badge and subscription-related buttons
    if (freeMode) {
      if (panelTierBadge) panelTierBadge.style.display = 'none';
      if (panelUpgradeBtn) panelUpgradeBtn.style.display = 'none';
      if (panelHistoryBtn) panelHistoryBtn.style.display = 'none';
    } else {
      if (panelTierBadge) panelTierBadge.style.display = '';
      if (panelUpgradeBtn) panelUpgradeBtn.style.display = '';
      if (panelHistoryBtn) panelHistoryBtn.style.display = '';
    }

    // Update icon button to show logged-in state
    if (accountIconBtn) {
      accountIconBtn.classList.add('logged-in');
    }
  } else {
    loggedOut.style.display = 'block';
    loggedIn.style.display = 'none';

    if (accountIconBtn) {
      accountIconBtn.classList.remove('logged-in');
    }
  }
}

/**
 * Update live mode button state
 */
export function updateLiveModeButton(isActive) {
  const liveBtn = document.querySelector('.icon-btn[data-action="live"]');
  if (liveBtn) {
    if (isActive) {
      liveBtn.classList.add('active', 'pulsing');
    } else {
      liveBtn.classList.remove('active', 'pulsing');
    }
  }
}

/**
 * Update export button state (disabled when no files)
 */
export function updateExportButtonState(hasFiles) {
  const exportBtn = document.querySelector('.icon-btn[data-panel="export"]');
  console.log('[OverlayPanels] updateExportButtonState:', hasFiles, 'button found:', !!exportBtn);
  if (exportBtn) {
    exportBtn.disabled = !hasFiles;
    exportBtn.classList.toggle('disabled', !hasFiles);
    console.log('[OverlayPanels] Export button disabled set to:', exportBtn.disabled);
  }
}

/**
 * Show transport bar
 */
export function showTransportBar() {
  const transportBar = document.getElementById('transportBar');
  if (transportBar) {
    transportBar.classList.add('visible');
  }
}

/**
 * Hide transport bar
 */
export function hideTransportBar() {
  const transportBar = document.getElementById('transportBar');
  if (transportBar) {
    transportBar.classList.remove('visible');
  }
}

/**
 * Check if settings is open
 */
export function isSettingsOpen() {
  return settingsOpen;
}

/**
 * Get active panel name
 */
export function getActivePanel() {
  return activePanel;
}
